/**
 * Chart Context Constants
 */

export const CHART_CONTEXT_LIMITS = {
  /** Maximum number of charts per context */
  MAX_CHARTS_PER_CONTEXT: 3,

  /** Size limits (bytes) */
  MAX_PER_CHART_BYTES: 60_000, // 60KB per chart
  MAX_TOTAL_BYTES: 180_000, // 180KB total

  /** Skip minification if original size is below this threshold */
  SKIP_MINIFY_THRESHOLD_BYTES: 30_000, // Don't minify if < 30KB

  /** Threshold for truncating data points (per chart, after minification) */
  TRUNCATE_THRESHOLD_BYTES: 30_000, // Start truncating at 30KB

  /** Maximum data points per series before truncation */
  MAX_DATA_POINTS_PER_SERIES: 100,

  /** Minimum data points to keep per series when truncating */
  MIN_DATA_POINTS_PER_SERIES: 20,
};

/**
 * Properties to remove from Highcharts options.
 * These are UI-only and not useful for LLM analysis.
 */
export const PROPERTIES_TO_REMOVE: string[] = [
  // UI/visual styling
  'tooltip',
  'credits',
  'exporting',
  'legend',
  'accessibility',
  'responsive',
  'navigation',

  // Chart container styling (not data-relevant)
  'chart.animation',
  'chart.backgroundColor',
  'chart.borderColor',
  'chart.borderRadius',
  'chart.borderWidth',
  'chart.className',
  'chart.colorCount',
  'chart.displayErrors',
  'chart.events',
  'chart.height',
  'chart.width',
  'chart.margin',
  'chart.marginBottom',
  'chart.marginLeft',
  'chart.marginRight',
  'chart.marginTop',
  'chart.spacing',
  'chart.spacingBottom',
  'chart.spacingLeft',
  'chart.spacingRight',
  'chart.spacingTop',
  'chart.style',
  'chart.zoomType',
  'chart.panning',
  'chart.panKey',
  'chart.shadow',
  'chart.reflow',
  'chart.renderTo',
  'chart.plotBorderColor',
  'chart.plotBorderWidth',
  'chart.plotShadow',
  'chart.selectionMarkerFill',
  'chart.resetZoomButton',

  // Title styling (keep text though)
  'title.style',
  'title.align',
  'title.floating',
  'title.margin',
  'title.useHTML',
  'title.verticalAlign',
  'title.widthAdjust',
  'title.x',
  'title.y',

  'subtitle',

  // Axis styling
  'xAxis.labels.style',
  'xAxis.labels.useHTML',
  'xAxis.labels.rotation',
  'xAxis.labels.align',
  'xAxis.labels.autoRotation',
  'xAxis.labels.autoRotationLimit',
  'xAxis.labels.padding',
  'xAxis.labels.reserveSpace',
  'xAxis.labels.x',
  'xAxis.labels.y',
  'xAxis.lineColor',
  'xAxis.lineWidth',
  'xAxis.tickColor',
  'xAxis.tickLength',
  'xAxis.tickWidth',
  'xAxis.gridLineColor',
  'xAxis.gridLineDashStyle',
  'xAxis.gridLineWidth',
  'xAxis.minorGridLineColor',
  'xAxis.minorGridLineDashStyle',
  'xAxis.minorGridLineWidth',
  'xAxis.minorTickColor',
  'xAxis.minorTickLength',
  'xAxis.minorTickWidth',
  'xAxis.minPadding',
  'xAxis.maxPadding',
  'xAxis.offset',
  'xAxis.opposite',
  'xAxis.showFirstLabel',
  'xAxis.showLastLabel',
  'xAxis.startOnTick',
  'xAxis.endOnTick',

  'yAxis.labels.style',
  'yAxis.labels.useHTML',
  'yAxis.labels.rotation',
  'yAxis.labels.align',
  'yAxis.labels.x',
  'yAxis.labels.y',
  'yAxis.lineColor',
  'yAxis.lineWidth',
  'yAxis.tickColor',
  'yAxis.tickLength',
  'yAxis.tickWidth',
  'yAxis.gridLineColor',
  'yAxis.gridLineDashStyle',
  'yAxis.gridLineWidth',
  'yAxis.minorGridLineColor',
  'yAxis.minorGridLineDashStyle',
  'yAxis.minorGridLineWidth',
  'yAxis.minorTickColor',
  'yAxis.minorTickLength',
  'yAxis.minorTickWidth',
  'yAxis.minPadding',
  'yAxis.maxPadding',
  'yAxis.offset',
  'yAxis.opposite',
  'yAxis.showFirstLabel',
  'yAxis.showLastLabel',
  'yAxis.startOnTick',
  'yAxis.endOnTick',
  'yAxis.visible',
  'yAxis.title.style',
  'yAxis.title.align',
  'yAxis.title.margin',
  'yAxis.title.x',
  'yAxis.title.y',

  // plotOptions styling (keep functional parts)
  'plotOptions.series.animation',
  'plotOptions.series.borderColor',
  'plotOptions.series.borderWidth',
  'plotOptions.series.cursor',
  'plotOptions.series.marker',
  'plotOptions.series.states',
  'plotOptions.series.point.events',
  'plotOptions.series.dataLabels.style',
  'plotOptions.series.dataLabels.color',
  'plotOptions.series.dataLabels.backgroundColor',
  'plotOptions.series.dataLabels.borderColor',
  'plotOptions.series.dataLabels.borderRadius',
  'plotOptions.series.dataLabels.borderWidth',
  'plotOptions.series.dataLabels.padding',
  'plotOptions.series.dataLabels.shadow',
  'plotOptions.series.dataLabels.useHTML',
  'plotOptions.series.dataLabels.crop',
  'plotOptions.series.dataLabels.overflow',
  'plotOptions.series.enableMouseTracking',
  'plotOptions.series.stickyTracking',
  'plotOptions.series.shadow',
  'plotOptions.series.showCheckbox',
  'plotOptions.series.showInLegend',
  'plotOptions.series.turboThreshold',
  'plotOptions.series.findNearestPointBy',
  'plotOptions.series.linecap',

  'plotOptions.pie.cursor',
  'plotOptions.pie.dataLabels',
  'plotOptions.pie.borderWidth',
  'plotOptions.pie.slicedOffset',
  'plotOptions.pie.states',
  'plotOptions.pie.shadow',
  'plotOptions.pie.showInLegend',

  'plotOptions.column.borderColor',
  'plotOptions.column.borderWidth',
  'plotOptions.column.borderRadius',
  'plotOptions.column.cursor',
  'plotOptions.column.dataLabels.style',
  'plotOptions.column.dataLabels.color',
  'plotOptions.column.shadow',
  'plotOptions.column.states',

  'plotOptions.spline.marker',
  'plotOptions.spline.lineWidth',
  'plotOptions.spline.cursor',
  'plotOptions.spline.shadow',
  'plotOptions.spline.states',
];

/**
 * Properties to always keep in series data points.
 * Everything else can be stripped for size reduction.
 */
export const SERIES_DATA_POINT_KEEP_PROPERTIES: string[] = [
  'name',
  'y',
  'x',
  'value',
  'percentage',
  'fieldId',
  'id',
  'weekId',
  'meta',
];

/**
 * Series-level properties to remove (UI-only).
 */
export const SERIES_PROPERTIES_TO_REMOVE: string[] = [
  'marker',
  'states',
  'cursor',
  'animation',
  'shadow',
  'borderColor',
  'borderWidth',
  'borderRadius',
  'enableMouseTracking',
  'stickyTracking',
  'showInLegend',
  'cropThreshold',
  'turboThreshold',
  'findNearestPointBy',
  'linecap',
  'pointPadding',
  'groupPadding',
  'pointWidth',
  'dataLabels',
  'zIndex',
];
